(() => {
  if (customElements.get('thauya-cart-rewards')) return;

  const memory = new Map();
  let pendingCart = null;

  const readState = key => {
    if (memory.has(key)) return memory.get(key);

    try {
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch (_) {
      return null;
    }
  };

  const writeState = (key, state) => {
    memory.set(key, state);

    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch (_) {}
  };

  const progress = (total, goals) => {
    if (total >= goals[2]) return 100;

    if (total >= goals[1]) {
      return 60 + 40 * (total - goals[1]) / (goals[2] - goals[1]);
    }

    if (total >= goals[0]) {
      return 20 + 40 * (total - goals[0]) / (goals[1] - goals[0]);
    }

    return Math.max(0, 20 * total / goals[0]);
  };

  // Share one in-flight cart read between reward widgets.
  const loadCart = url => {
    if (!pendingCart) {
      pendingCart = fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store'
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Cart request: HTTP ${response.status}`);
          }

          return response.json();
        })
        .finally(() => {
          pendingCart = null;
        });
    }

    return pendingCart;
  };

  customElements.define(
    'thauya-cart-rewards',
    class extends HTMLElement {
      connectedCallback() {
        if (this.active) return;

        this.active = true;
        this.generation = (this.generation || 0) + 1;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.events = [
          'cart:updated',
          'cart:update',
          'cart:refresh',
          'cart:change'
        ];
        this.cleanups = [];

        try {
          this.config = JSON.parse(
            this.querySelector('[data-cr-config]')?.textContent || 'null'
          );

          const milestones = this.config?.milestones;

          if (!Array.isArray(milestones) || milestones.length !== 3) {
            throw new Error('Three checkpoints are required.');
          }

          this.goals = milestones.map(m => Number(m.goal));

          if (
            !this.goals.every(Number.isFinite) ||
            this.goals[0] <= 0 ||
            this.goals[1] <= this.goals[0] ||
            this.goals[2] <= this.goals[1]
          ) {
            throw new Error(
              'Checkpoint amounts must be positive and ascending.'
            );
          }

          this.fill = this.querySelector('.cr-fill');
          this.track = this.querySelector('.cr-track');
          this.points = [...this.querySelectorAll('[data-cr-point]')];
          this.message = this.querySelector('[data-cr-message]');

          if (
            !this.fill ||
            !this.track ||
            !this.message ||
            this.points.length !== 3
          ) {
            throw new Error('Reward markup is incomplete.');
          }

          this.toast = this.querySelector('[data-cr-toast]');
          this.toastMessage = this.querySelector('[data-cr-toast-message]');
          this.currency = this.dataset.currency || 'INR';
          this.setKey();

          this.points.forEach(point => {
            const icon = point.querySelector('.cr-icon');

            if (icon) {
              icon.innerHTML = `
                <svg
                  class="cr-lock-svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    class="cr-shackle"
                    d="M7 10V7a5 5 0 0 1 10 0v3"
                  ></path>
                  <rect
                    x="4"
                    y="10"
                    width="16"
                    height="11"
                    rx="2.5"
                    fill="white"
                  ></rect>
                  <circle
                    cx="12"
                    cy="15"
                    r="1.2"
                    fill="currentColor"
                    stroke="none"
                  ></circle>
                  <path d="M12 16v1.5"></path>
                </svg>
              `;
            }
          });

          const saved = readState(this.key);
          const serverTotal = Number(this.config.total);
          const initial = Number.isFinite(saved?.total)
            ? saved.total
            : Number.isFinite(serverTotal)
              ? serverTotal
              : 0;
          const nonempty = !this.hidden;

          this.classList.remove('cr-motion-ready');
          this.render(initial, nonempty, false, false);
          void this.fill.offsetWidth;

          this.dataset.crReady = 'true';
          this.dataset.crVersion = '7';

          this.frame = requestAnimationFrame(() => {
            if (!this.active) return;

            this.classList.add('cr-motion-ready');

            if (Number.isFinite(serverTotal)) {
              this.render(serverTotal, nonempty, true, true);
            }
          });

          this.schedule = () => {
            if (!this.active) return;

            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
              if (this.active && this.isConnected) {
                this.refresh();
              }
            }, 350);
          };

          const listen = (target, name, handler) => {
            target.addEventListener(name, handler);
            this.cleanups.push(() => {
              target.removeEventListener(name, handler);
            });
          };

          this.events.forEach(name => {
            listen(document, name, this.schedule);
          });

          listen(window, 'pageshow', this.schedule);

          listen(document, 'visibilitychange', () => {
            if (!document.hidden) this.schedule();
          });

          listen(document, 'change', event => {
            if (
              event.target instanceof Element &&
              event.target.matches(
                'input[name="updates[]"],' +
                'input[name^="updates["],' +
                'input[name="quantity"]'
              )
            ) {
              this.schedule();
            }
          });

          listen(this, 'click', event => {
            if (
              event.target instanceof Element &&
              event.target.closest('.cr-dismiss')
            ) {
              const toast = this.querySelector('[data-cr-toast]');
              if (toast) toast.hidden = true;
            }
          });

          if (
            typeof subscribe === 'function' &&
            typeof PUB_SUB_EVENTS !== 'undefined' &&
            PUB_SUB_EVENTS.cartUpdate
          ) {
            this.unsubscribe = subscribe(
              PUB_SUB_EVENTS.cartUpdate,
              this.schedule
            );
          }

          const selector = [
            'cart-items-component',
            'cart-drawer-component',
            'cart-drawer',
            'cart-items',
            '#main-cart-items',
            '#main-cart-footer'
          ].join(',');

          // Structural changes only. Do not observe style/class writes
          // made by this widget or its animations.
          const observerOptions = {
            childList: true,
            subtree: true
          };

          this.observer = new MutationObserver(records => {
            if (!this.active || !this.isConnected) return;

            const relevant = records.some(record => {
              const target = record.target instanceof Element
                ? record.target
                : record.target.parentElement;

              if (!target) return false;

              // Ignore rendering inside any rewards widget.
              if (target.closest('thauya-cart-rewards')) return false;

              if (target.closest(selector)) return true;

              return [
                ...record.addedNodes,
                ...record.removedNodes
              ].some(node => {
                return node instanceof Element && (
                  node.matches(selector) ||
                  node.querySelector(selector) ||
                  node.matches('thauya-cart-rewards') ||
                  node.querySelector('thauya-cart-rewards')
                );
              });
            });

            if (!relevant) return;

            // Never observe mutations generated by restoration itself.
            this.observer.disconnect();

            try {
              this.restoreMarkup();
            } finally {
              if (this.active && this.isConnected) {
                this.observer.observe(document.body, observerOptions);
              }
            }

            this.schedule();
          });

          this.observer.observe(document.body, observerOptions);
          this.schedule();
        } catch (error) {
          this.disconnectedCallback();
          console.warn('Cart rewards setup:', error.message);
        }
      }

      setKey() {
        this.key = [
          'thauya-rewards-v4',
          this.currency,
          this.config.basis,
          this.goals.join('-')
        ].join(':');
      }

      disconnectedCallback() {
        this.active = false;
        this.generation = (this.generation || 0) + 1;

        clearTimeout(this.timer);
        clearTimeout(this.toastTimer);
        cancelAnimationFrame(this.frame);

        (this.cleanups || []).forEach(cleanup => cleanup());
        this.cleanups = [];

        if (typeof this.unsubscribe === 'function') {
          this.unsubscribe();
        }

        this.unsubscribe = null;
        this.observer?.disconnect();
      }

      money(cents) {
        const currency = this.currency || this.dataset.currency || 'INR';
        const amount = Number(cents) / 100;

        if (currency === 'INR') {
          return '₹' + new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(amount);
        }

        try {
          return new Intl.NumberFormat(
            document.documentElement.lang || 'en',
            {
              style: 'currency',
              currency,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }
          ).format(amount);
        } catch (_) {
          return amount.toFixed(2) + ' ' + currency;
        }
      }

      restoreMarkup() {
        const fill = this.querySelector('.cr-fill');
        const track = this.querySelector('.cr-track');
        const message = this.querySelector('[data-cr-message]');
        const points = [...this.querySelectorAll('[data-cr-point]')];

        if (!fill || !track || !message || points.length !== 3) {
          return false;
        }

        const replaced =
          fill !== this.fill ||
          track !== this.track ||
          message !== this.message ||
          points.some((point, index) => {
            return point !== this.points?.[index];
          });

        const stateReset =
          Array.isArray(this.lastReached) &&
          points.some((point, index) => {
            return (
              point.classList.contains('is-reached') !==
              this.lastReached[index]
            );
          });

        // CSS may round fractional percentages when serializing width.
        const displayedPercent = Number.parseFloat(fill.style.width);
        const positionReset =
          Number.isFinite(this.lastPercent) && (
            !Number.isFinite(displayedPercent) ||
            Math.abs(displayedPercent - this.lastPercent) > 0.01
          );

        this.fill = fill;
        this.track = track;
        this.message = message;
        this.points = points;
        this.toast = this.querySelector('[data-cr-toast]');
        this.toastMessage = this.querySelector('[data-cr-toast-message]');

        if (this.dataset.crReady !== 'true') {
          this.dataset.crReady = 'true';
        }

        if (this.dataset.crVersion !== '7') {
          this.dataset.crVersion = '7';
        }

        if (
          (replaced || stateReset || positionReset) &&
          Number.isFinite(this.lastPercent)
        ) {
          this.classList.remove('cr-motion-ready');

          fill.style.width = this.lastPercent + '%';

          points.forEach((point, index) => {
            point.classList.toggle(
              'is-reached',
              Boolean(this.lastReached?.[index])
            );
          });

          this.classList.toggle(
            'is-complete',
            Boolean(this.lastReached?.every(Boolean))
          );

          if (
            typeof this.lastMessage === 'string' &&
            message.textContent !== this.lastMessage
          ) {
            message.textContent = this.lastMessage;
          }

          track.setAttribute(
            'aria-valuenow',
            String(Math.round(this.lastPercent))
          );

          if (typeof this.lastMessage === 'string') {
            track.setAttribute('aria-valuetext', this.lastMessage);
          }

          void fill.offsetWidth;
          this.classList.add('cr-motion-ready');

          return true;
        }

        return false;
      }

      render(total, nonempty, celebrate, persist) {
        this.restoreMarkup();

        total = nonempty ? Math.max(0, total) : 0;

        const previous = this.lastReached;
        const reached = this.goals.map(goal => nonempty && total >= goal);
        const percent = Math.max(
          0,
          Math.min(100, progress(total, this.goals))
        );

        this.hidden = !nonempty;
        this.classList.toggle('is-complete', reached.every(Boolean));

        const displayedPercent = Number.parseFloat(this.fill.style.width);

        if (
          !Number.isFinite(displayedPercent) ||
          Math.abs(displayedPercent - percent) > 0.01
        ) {
          this.fill.style.width = percent + '%';
        }

        this.track.setAttribute(
          'aria-valuenow',
          String(Math.round(percent))
        );

        const index = reached.findIndex(value => !value);
        const next = this.config.milestones[index];

        const message = index < 0
          ? (this.config.complete || '')
          : String(
              this.config.next || 'Add [amount] more for [reward]'
            )
              .replaceAll(
                '[amount]',
                this.money(Math.max(0, this.goals[index] - total))
              )
              .replaceAll('[reward]', String(next.label || ''));

        if (this.message.textContent !== message) {
          this.message.textContent = message;
        }

        this.track.setAttribute('aria-valuetext', message);

        this.points.forEach((point, pointIndex) => {
          point.classList.toggle('is-reached', reached[pointIndex]);

          const goal = point.querySelector('[data-cr-goal]');

          if (goal) {
            const formatted = this.money(this.goals[pointIndex]);

            if (goal.textContent !== formatted) {
              goal.textContent = formatted;
            }
          }
        });

        if (
          celebrate &&
          previous &&
          nonempty &&
          this.config.popups &&
          this.toast &&
          this.toastMessage
        ) {
          const newlyReached = this.config.milestones.filter(
            (_, milestoneIndex) => {
              return reached[milestoneIndex] && !previous[milestoneIndex];
            }
          );

          if (newlyReached.length) {
            this.toastMessage.textContent = newlyReached
              .map(m => m.popup || m.label || '')
              .join(' ');

            this.toast.hidden = false;
            clearTimeout(this.toastTimer);

            this.toastTimer = setTimeout(() => {
              if (this.toast) this.toast.hidden = true;
            }, 8000);
          }
        }

        if (!nonempty && this.toast) {
          this.toast.hidden = true;
        }

        this.lastReached = reached;
        this.lastPercent = percent;
        this.lastMessage = message;

        if (persist) {
          writeState(this.key, { total, reached });
        }
      }

      async refresh() {
        const generation = this.generation;

        try {
          const url = this.dataset.cartUrl;

          if (!url) {
            throw new Error('Cart URL is missing.');
          }

          const cart = await loadCart(url);

          if (
            !this.active ||
            !this.isConnected ||
            this.generation !== generation
          ) {
            return;
          }

          const total = this.config.basis === 'original'
            ? cart.original_total_price
            : cart.total_price;

          if (
            !Number.isFinite(total) ||
            !Number.isFinite(cart.item_count)
          ) {
            throw new Error('Cart response is incomplete.');
          }

          if (cart.currency && cart.currency !== this.currency) {
            this.currency = cart.currency;
            this.dataset.currency = cart.currency;
            this.setKey();

            this.classList.remove('cr-motion-ready');
            this.render(total, cart.item_count > 0, false, true);
            void this.fill.offsetWidth;
            this.classList.add('cr-motion-ready');
          } else {
            this.render(total, cart.item_count > 0, true, true);
          }
        } catch (error) {
          // Preserve the displayed progress when a cart read fails.
          console.warn('Cart rewards could not refresh:', error.message);
        }
      }
    }
  );
})();
